// Monopath: every welcome-menu pick commits the goals-system branch, so the
// only QA "path" is System. (Debt-focused members are still covered — they run
// the System plan with high-interest debt; see the debt-type cases below.)
export type ConversationalQAPath = 'System';

export type ConversationalQADebtType =
    | 'High Interest'
    | 'Low Interest'
    | 'Both'
    | 'Custom'
    | 'None';

/**
 * A "detour" is an off-happy-path behavior the persona performs mid-conversation
 * (the kind of thing that historically produces bugs). It layers a behavioral
 * script on top of a base financial profile — e.g. answering the debt questions,
 * then remembering another debt at the income step. The `personaInstructions`
 * MUST pin down WHEN the member acts (which agent question triggers the detour)
 * so the timing is deterministic across runs.
 */
export interface ConversationalQADetour {
    /** Stable id suffix, e.g. 'forgot-personal-loans'. */
    id: string;
    /** Short label shown in the case name, e.g. 'detour: forgot personal loans'. */
    label: string;
    /** Behavioral script appended to the persona (must specify the trigger point). */
    personaInstructions: string;
}

export interface ConversationalQATestCase {
    id: string;
    name: string;
    path: ConversationalQAPath;
    debtType: ConversationalQADebtType;
    income: number;
    bills: number;
    spend: number;
    goals: number;
    debt: number;
    savingsGoal: number;
    outcomeInstructions: string;
    /** Optional off-happy-path behavior layered onto this case. */
    detour?: ConversationalQADetour;
}

const makeOutcomeInstructions = (allocation: string): string =>
    `The Money Map generated after the conversation should split the money the member puts toward goals as roughly ${allocation}. This breakdown is expected to be reflected implicitly in the generated Money Map, not spelled out in the conversation. Do NOT require or expect the advisor to explain, recommend, or verbalize this split (or these percentages) during the chat — only judge the allocation against the outcome captured after the conversation.`;

const OUTCOME_SYSTEM_V2_NO_DEBT = makeOutcomeInstructions(
    'Emergency Fund 50%, Savings Goal 40%, Retirement 10%',
);

const OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE = makeOutcomeInstructions(
    'Debt 70%, Savings Goal 15%, Emergency Fund 10%, Retirement 5%',
);

const OUTCOME_SYSTEM_V2_DEBT_BALANCED = makeOutcomeInstructions(
    'Debt 40%, Savings Goal 20%, Emergency Fund 30%, Retirement 10%',
);

const toId = (value: string): string =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');

/* eslint-disable @typescript-eslint/max-params -- positional arguments keep the QA catalog readable */
const makeCase = (
    name: string,
    path: ConversationalQAPath,
    debtType: ConversationalQADebtType,
    income: number,
    bills: number,
    spend: number,
    goals: number,
    debt: number,
    savingsGoal: number,
    outcomeInstructions: string,
): ConversationalQATestCase => ({
    id: toId(`${path}-${debtType}-${name}`),
    name,
    path,
    debtType,
    income,
    bills,
    spend,
    goals,
    debt,
    savingsGoal,
    outcomeInstructions,
});
/* eslint-enable @typescript-eslint/max-params */

/**
 * Layer a detour onto a base case: reuses the base's financial profile but
 * appends the detour's behavioral script (via buildPersonaFromTestCase) and
 * swaps in an outcome that reflects the detour's effect on the Money Map. The
 * id/name get a detour suffix so it lists (and scores) as its own case.
 */
const makeDetourCase = (
    base: ConversationalQATestCase,
    detour: ConversationalQADetour,
    outcomeInstructions: string,
): ConversationalQATestCase => ({
    ...base,
    id: toId(`${base.path}-${base.debtType}-${base.name}-${detour.id}`),
    name: `${base.name} (${detour.label})`,
    detour,
    outcomeInstructions,
});

/**
 * Detour: the member picks only credit cards up front, then remembers a
 * personal loan at the income step — exercising the backward debt-correction
 * path (income reconciler re-opens debt, collects the balance conversationally,
 * re-shows the table, then resumes income).
 */
const FORGOT_PERSONAL_LOANS_DETOUR: ConversationalQADetour = {
    id: 'forgot-personal-loans',
    label: 'detour: forgot personal loans',
    personaInstructions:
        'IMPORTANT — you have a debt you forget at first. The ~$10,000 you think of as your debt is your CREDIT CARDS. You ALSO owe about $8,000 in personal loans, but it slips your mind at the start. Play it out exactly like this: (1) When the high-interest debt menu appears, pick ONLY "Credit cards" — do NOT mention personal loans yet. (2) The FIRST time the assistant asks about your monthly income, do NOT give your income yet — instead say something like "oh wait, I actually also have about $8,000 in personal loans I forgot to mention." (3) If the assistant then asks for the personal-loan balance, answer with about $8,000, and approve the updated debt summary if one is shown. (4) Once the personal loan is squared away, answer the income question normally the next time it comes up. Bring up the personal loans only once — do not repeat it later.',
};

const OUTCOME_DETOUR_FORGOT_PERSONAL_LOANS = `${OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE} The member also remembered a ~$8,000 personal loan partway through the conversation (after the debt table was first confirmed), so the final Money Map's debt must include BOTH the ~$10,000 in credit cards AND the ~$8,000 personal loan (~$18,000 total) — not just the credit cards.`;

/**
 * Detour: like forgot-personal-loans, but the member states the balance in the
 * SAME message they surface the debt — exercising the inline-balance branch of
 * CORRECTING_CONFIRMED_DEBT (the assistant must NOT re-ask for the amount).
 */
const FORGOT_DEBT_INLINE_BALANCE_DETOUR: ConversationalQADetour = {
    id: 'forgot-debt-inline-balance',
    label: 'detour: forgot a debt, balance stated inline',
    personaInstructions:
        'IMPORTANT — you have a debt you forget at first. The ~$10,000 you think of as your debt is your CREDIT CARDS. You ALSO owe about $6,000 in personal loans, but it slips your mind at the start. Play it out exactly like this: (1) When the high-interest debt menu appears, pick ONLY "Credit cards" — do NOT mention personal loans yet. (2) The FIRST time the assistant asks about your monthly income, do NOT give your income yet — instead, in ONE sentence that INCLUDES the amount, say "oh wait, I also have about $6,000 in personal loans I forgot to mention." Always state the ~$6,000 in that same message. (3) Approve the updated debt summary if one is shown. (4) Once the personal loan is squared away, answer the income question normally the next time it comes up. Bring up the personal loans only once.',
};

const OUTCOME_DETOUR_FORGOT_DEBT_INLINE = `${OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE} The member remembered a personal loan partway through and gave its ~$6,000 balance in the same message, so the final Money Map's debt must include BOTH the ~$10,000 in credit cards AND the ~$6,000 personal loan (~$16,000 total).`;

/**
 * Detour: member says they're solo at the partner question, then reveals a
 * partner's income at the income step — exercising the income reconciler's
 * "partner's income added later" path (which must call check-household and
 * combine both take-homes into a joint household).
 */
const PARTNER_INCOME_LATE_DETOUR: ConversationalQADetour = {
    id: 'partner-income-late',
    label: 'detour: forgot partner income',
    personaInstructions:
        'IMPORTANT — you actually plan your finances WITH your partner, but you forget to mention them at first. Play it out exactly like this: (1) When asked whether you\u2019re planning your finances with a partner (a yes/no choice), answer NO — just you. (2) The FIRST time the assistant asks about your monthly income, give YOUR take-home (about $5,000/month) AND in the same message add that you forgot your partner also brings in about $3,000/month take-home. (3) From then on, treat it as a joint household and answer normally. Surface the partner only once.',
};

const OUTCOME_DETOUR_PARTNER_INCOME = `${OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE} The member first said they had no partner, then revealed at the income step that their partner also earns ~$3,000/month take-home. The final Money Map must reflect a JOINT household with combined monthly take-home of ~$8,000 (the member's ~$5,000 plus the partner's ~$3,000), not just $5,000.`;

/**
 * Detour: member corrects their income after it was already confirmed, while
 * reviewing bills — exercising the bills reconciler's backward income-correction
 * path (recalculate, re-check, re-submit income, then return to bills).
 */
const CORRECT_INCOME_AT_BILLS_DETOUR: ConversationalQADetour = {
    id: 'correct-income-at-bills',
    label: 'detour: corrects income at bills',
    personaInstructions:
        'IMPORTANT — you misstate your income at first and fix it later. Play it out exactly like this: (1) When asked about your monthly income, say about $5,000/month take-home, and approve the income figure when the menu appears. (2) The FIRST time you\u2019re looking at the estimated monthly bills, before approving them, say something like "actually, I gave the wrong number earlier — my take-home is really about $6,000/month." (3) After the assistant fixes your income, approve the bills and continue normally. Correct the income only once.',
};

const OUTCOME_DETOUR_CORRECT_INCOME = `${OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE} The member corrected their income at the bills step from ~$5,000 to ~$6,000/month take-home, so the final Money Map must be built on the corrected ~$6,000/month figure, not the original $5,000.`;

/**
 * Detour: member edits a bill estimate before approving — exercising the bills
 * reconciler's "change a bill" path (re-show the merged table, then submit on
 * approval).
 */
const CHANGE_A_BILL_DETOUR: ConversationalQADetour = {
    id: 'change-a-bill',
    label: 'detour: edits a bill estimate',
    personaInstructions:
        'IMPORTANT — the bill estimate is off and you fix it. Play it out exactly like this: (1) Answer the debt and income questions normally. (2) When the estimated monthly bills are shown, do NOT approve them yet — first say that your rent is actually about $2,200 a month, higher than the estimate. (3) After the assistant updates the bills, approve the revised table and continue. Change the rent only once.',
};

const OUTCOME_DETOUR_CHANGE_A_BILL = `${OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE} The member raised their rent to ~$2,200/month at the bills step, so the final Money Map's monthly expenses must reflect the updated rent (a higher total than the original estimate).`;

/**
 * System-path detour: member says they have no high-interest debt, then remembers
 * ~$8,000 in credit cards at the SAVINGS step (the last step, unique to the System
 * plan) — exercising the savings reconciler's debt side-trip (re-open debt, collect
 * the balance, re-show the table, then resume the savings goal).
 */
const FORGOT_DEBT_AT_SAVINGS_DETOUR: ConversationalQADetour = {
    id: 'forgot-debt-at-savings',
    label: 'detour: forgot debt until savings',
    personaInstructions:
        'IMPORTANT — you actually have credit-card debt, but you forget it until the very end. Play it out exactly like this: (1) When the high-interest debt menu appears, pick "No high-interest debt" — you don\'t think you have any yet. (2) Answer the income and bills questions normally. (3) When it\'s time for your savings goal, pick your goal from the menu as usual, but the FIRST time the assistant asks HOW MUCH you want to save, do NOT give the amount yet — instead say "oh wait, I actually have about $8,000 in credit card debt I completely forgot about." (4) If the assistant asks anything to confirm that debt, approve it. (5) Once the debt is squared away, answer the savings amount question normally the next time it comes up (about $30,000). Bring up the credit-card debt only once.',
};

const OUTCOME_DETOUR_FORGOT_DEBT_AT_SAVINGS = `${OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE} The member first said they had no high-interest debt, then remembered ~$8,000 in credit card debt at the savings step. The final Money Map must treat this as a debt-carrying system — the ~$8,000 in credit cards must appear as high-interest debt to pay down, alongside the ~$30,000 savings goal — NOT as a no-debt plan.`;

/**
 * System-path detour: member says they're solo, then reveals a partner's income at
 * the SAVINGS step — exercising the savings reconciler's add-partner-income path
 * (must call check-household, recompute the joint take-home, then resume savings).
 */
const FORGOT_PARTNER_AT_SAVINGS_DETOUR: ConversationalQADetour = {
    id: 'forgot-partner-at-savings',
    label: 'detour: forgot partner until savings',
    personaInstructions:
        'IMPORTANT — you actually plan your finances WITH your partner, but you forget to mention them until the very end. Play it out exactly like this: (1) When asked whether you\u2019re planning your finances with a partner (a yes/no choice), answer NO — just you. (2) Answer the income and bills questions normally for just yourself (about $5,000/month take-home). (3) When it\'s time for your savings goal, pick your goal from the menu as usual, but the FIRST time the assistant asks HOW MUCH you want to save, do NOT give the amount yet — instead say "oh, I should mention my partner and I actually do this together — they bring in about $3,000/month too." (4) From then on treat it as a joint household. (5) Once that\'s sorted, answer the savings amount question normally the next time it comes up (about $30,000). Surface the partner only once.',
};

const OUTCOME_DETOUR_FORGOT_PARTNER_AT_SAVINGS = `${OUTCOME_SYSTEM_V2_NO_DEBT} The member first said they had no partner, then at the savings step revealed a partner earning ~$3,000/month take-home. The final Money Map must reflect a JOINT household with combined monthly take-home of ~$8,000 (the member's ~$5,000 plus the partner's ~$3,000), not just $5,000.`;

export const CONVERSATIONAL_QA_TEST_CASES: ConversationalQATestCase[] = [
    // TEMP: detours pinned to the top for testing — move back down when done.
    // The income/bills-step detours all layer onto the same System / High
    // Interest happy-path profile (5000 income, 3000 bills, 1000 spend, 1000
    // goals, 10000 debt, 25000 savings goal) so the only variable under test is
    // the off-happy-path behavior itself.
    makeDetourCase(
        makeCase(
            'System - High interest debt, happy path',
            'System',
            'High Interest',
            5000,
            3000,
            1000,
            1000,
            10000,
            25000,
            OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
        ),
        FORGOT_PERSONAL_LOANS_DETOUR,
        OUTCOME_DETOUR_FORGOT_PERSONAL_LOANS,
    ),
    makeDetourCase(
        makeCase(
            'System - High interest debt, happy path',
            'System',
            'High Interest',
            5000,
            3000,
            1000,
            1000,
            10000,
            25000,
            OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
        ),
        FORGOT_DEBT_INLINE_BALANCE_DETOUR,
        OUTCOME_DETOUR_FORGOT_DEBT_INLINE,
    ),
    makeDetourCase(
        makeCase(
            'System - High interest debt, happy path',
            'System',
            'High Interest',
            5000,
            3000,
            1000,
            1000,
            10000,
            25000,
            OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
        ),
        PARTNER_INCOME_LATE_DETOUR,
        OUTCOME_DETOUR_PARTNER_INCOME,
    ),
    makeDetourCase(
        makeCase(
            'System - High interest debt, happy path',
            'System',
            'High Interest',
            5000,
            3000,
            1000,
            1000,
            10000,
            25000,
            OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
        ),
        CORRECT_INCOME_AT_BILLS_DETOUR,
        OUTCOME_DETOUR_CORRECT_INCOME,
    ),
    makeDetourCase(
        makeCase(
            'System - High interest debt, happy path',
            'System',
            'High Interest',
            5000,
            3000,
            1000,
            1000,
            10000,
            25000,
            OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
        ),
        CHANGE_A_BILL_DETOUR,
        OUTCOME_DETOUR_CHANGE_A_BILL,
    ),
    // System-path detours: layer onto the System / No debt happy-path profile
    // (5000 income, 3000 bills, 1000 spend, 1000 goals, no debt, 30000 goal) and
    // surface the deviation at the savings step, which only the System plan has.
    makeDetourCase(
        makeCase(
            'System - No debt, happy path',
            'System',
            'None',
            5000,
            3000,
            1000,
            1000,
            0,
            30000,
            OUTCOME_SYSTEM_V2_NO_DEBT,
        ),
        FORGOT_DEBT_AT_SAVINGS_DETOUR,
        OUTCOME_DETOUR_FORGOT_DEBT_AT_SAVINGS,
    ),
    makeDetourCase(
        makeCase(
            'System - No debt, happy path',
            'System',
            'None',
            5000,
            3000,
            1000,
            1000,
            0,
            30000,
            OUTCOME_SYSTEM_V2_NO_DEBT,
        ),
        FORGOT_PARTNER_AT_SAVINGS_DETOUR,
        OUTCOME_DETOUR_FORGOT_PARTNER_AT_SAVINGS,
    ),

    // System path — no debt
    makeCase(
        'System - No debt, happy path',
        'System',
        'None',
        5000,
        3000,
        1000,
        1000,
        0,
        30000,
        OUTCOME_SYSTEM_V2_NO_DEBT,
    ),
    makeCase(
        'System - No debt, little for goals',
        'System',
        'None',
        5000,
        4000,
        800,
        200,
        0,
        20000,
        OUTCOME_SYSTEM_V2_NO_DEBT,
    ),
    makeCase(
        'System - No debt, none for goals',
        'System',
        'None',
        5000,
        4000,
        1000,
        0,
        0,
        15000,
        OUTCOME_SYSTEM_V2_NO_DEBT,
    ),

    // System path — custom "other" high-interest debt (free-text "Something
    // else..."). Preserves the GoalsDebtType.Other free-text coverage the
    // removed Debt-path "Custom other debt" case had, now inside the System plan.
    makeCase(
        'System - Custom other debt',
        'System',
        'Custom',
        5000,
        3000,
        1000,
        1000,
        10000,
        25000,
        OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
    ),

    // System path — high-interest debt
    makeCase(
        'System - High interest debt, happy path',
        'System',
        'High Interest',
        5000,
        3000,
        1000,
        1000,
        10000,
        25000,
        OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
    ),
    makeCase(
        'System - High interest debt, little for goals',
        'System',
        'High Interest',
        5000,
        4000,
        800,
        200,
        10000,
        20000,
        OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
    ),
    makeCase(
        'System - High interest debt, high DTI',
        'System',
        'High Interest',
        5000,
        3500,
        1000,
        500,
        50000,
        30000,
        OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
    ),
    makeCase(
        'System - High interest debt, none for goals',
        'System',
        'High Interest',
        5000,
        4000,
        1000,
        0,
        10000,
        15000,
        OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
    ),

    // System path — low-interest debt only
    makeCase(
        'System - Low interest debt, happy path',
        'System',
        'Low Interest',
        5000,
        3000,
        1000,
        1000,
        10000,
        25000,
        OUTCOME_SYSTEM_V2_DEBT_BALANCED,
    ),
    makeCase(
        'System - Low interest debt, little for goals',
        'System',
        'Low Interest',
        5000,
        4000,
        800,
        200,
        10000,
        20000,
        OUTCOME_SYSTEM_V2_DEBT_BALANCED,
    ),
    makeCase(
        'System - Low interest debt, none for goals',
        'System',
        'Low Interest',
        5000,
        4000,
        1000,
        0,
        10000,
        15000,
        OUTCOME_SYSTEM_V2_DEBT_BALANCED,
    ),
    makeCase(
        'System - Low interest debt, high DTI',
        'System',
        'Low Interest',
        5000,
        3500,
        1000,
        500,
        50000,
        30000,
        OUTCOME_SYSTEM_V2_DEBT_BALANCED,
    ),

    // System path — both debt types
    makeCase(
        'System - Both debt types, happy path',
        'System',
        'Both',
        5000,
        3000,
        1000,
        1000,
        10000,
        25000,
        OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
    ),
    makeCase(
        'System - Both debt types, high DTI',
        'System',
        'Both',
        5000,
        3500,
        1000,
        500,
        50000,
        30000,
        OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
    ),
    makeCase(
        'System - Both debt types, little for goals',
        'System',
        'Both',
        5000,
        4000,
        800,
        200,
        10000,
        20000,
        OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
    ),
    makeCase(
        'System - Both debt types, none for goals',
        'System',
        'Both',
        5000,
        4000,
        1000,
        0,
        10000,
        15000,
        OUTCOME_SYSTEM_V2_DEBT_AGGRESSIVE,
    ),
];

const PRIMARY_GOAL_BY_PATH: Record<ConversationalQAPath, string> = {
    System: 'You want a complete financial system — you want structure and a plan built around your goals. On the "Where do you want to start?" welcome menu, pick "Create a budget or money system." You also have a specific savings goal you want to work toward (like a house down payment or vacation).',
};

const DEBT_TYPE_DESCRIPTION: Record<ConversationalQADebtType, string | null> = {
    'High Interest':
        'Your debt is high-interest debt, like credit-card balances.',
    'Low Interest':
        'Your debt is low-interest debt, like a student loan or car loan.',
    Both: 'Your debt is a mix of high-interest balances (credit cards) and low-interest balances (a student or car loan).',
    Custom: 'Your debt is a high-interest debt that does NOT match any of the listed options (not credit cards, not personal loans). When the high-interest debt menu appears, do NOT pick a preset option — instead use the "Something else..." free-text field to describe it in your own words, e.g. "a diamond loan" (an in-store financing plan). Answer the balance question with the total debt amount and keep your custom label.',
    None: null,
};

const formatDollars = (amount: number): string =>
    `$${amount.toLocaleString('en-US')}`;

export const buildPersonaFromTestCase = (
    testCase: ConversationalQATestCase,
): string => {
    const lines = [
        'You are a prospective Fruitful member going through onboarding.',
        `Your monthly take-home income is ${formatDollars(testCase.income)}.`,
        `Your monthly bills and fixed expenses are about ${formatDollars(testCase.bills)}.`,
        `Each month you spend roughly ${formatDollars(testCase.spend)} on discretionary things and put about ${formatDollars(testCase.goals)} toward your goals.`,
    ];

    if (testCase.debt > 0) {
        lines.push(
            `You currently have about ${formatDollars(testCase.debt)} in total debt.`,
        );
        const debtDescription = DEBT_TYPE_DESCRIPTION[testCase.debtType];
        if (debtDescription != null) {
            lines.push(debtDescription);
        }
    }

    if (testCase.savingsGoal > 0) {
        lines.push(
            `You are working toward a savings goal of about ${formatDollars(testCase.savingsGoal)}.`,
        );
    }

    lines.push(PRIMARY_GOAL_BY_PATH[testCase.path]);

    if (testCase.detour != null) {
        lines.push(testCase.detour.personaInstructions);
    }

    return lines.join(' ');
};
